import React from 'react';
import clsx from 'clsx';
import {
  TooltipProvider,
  Tooltip as TooltipWrapper,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'; // Ensure these are correctly imported

interface IconSimpleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selectedIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  size?: 24 | 28 | 32;
  state?: 'default' | 'selected' | 'selected-no-bg' | 'disabled';
  tooltipShow?: boolean;
  tooltipContent?: string;
  /**
   * When true, the button will not show background styles in the default (not selected) state
   */
  noBgWhenDefault?: boolean;
  /**
   * When true, the button displays a border similar to SecondaryButton
   */
  outlined?: boolean;
}

export const IconSimpleButton: React.FC<IconSimpleButtonProps> = ({
  className = '',
  icon: Icon,
  selectedIcon: SelectedIcon,
  size,
  state = 'default',
  tooltipShow,
  tooltipContent,
  noBgWhenDefault = false,
  outlined = false,
  ...rest
}) => {
  let sizeClass = 'w-6 h-6';
  if (size === 24) sizeClass = 'w-6 h-6';
  if (size === 28) sizeClass = 'w-7 h-7';
  if (size === 32) sizeClass = 'w-8 h-8';

  const isSelected = state === 'selected';
  const isSelectedNoBg = state === 'selected-no-bg';
  const isDisabled = state === 'disabled';
  const showSelectedIcon = (isSelected || isSelectedNoBg) && !!SelectedIcon;

  const buttonElement = (
    <button
      type="button"
      className={clsx(
        'flex items-center justify-center rounded-md p-1',
        sizeClass,
        isDisabled ? 'cursor-not-allowed text-neutral-300' : 'cursor-pointer',
        isSelected
          ? 'bg-neutral-100 text-neutral-500 hover:bg-neutral-100'
          : isSelectedNoBg
            ? 'bg-transparent text-neutral-500 hover:bg-transparent'
            : isDisabled
              ? 'bg-transparent'
              : noBgWhenDefault
                ? 'bg-transparent text-neutral-500 hover:bg-neutral-100'
                : 'text-neutral-500 hover:bg-neutral-100',
        outlined && 'border border-neutral-300',
        'aria-expanded:bg-neutral-100 data-[state=open]:bg-neutral-100',
        className,
      )}
      disabled={isDisabled}
      style={{ lineHeight: 0 }}
      {...rest}
    >
      {/* Show only one icon: selectedIcon if selected, else main icon */}
      {showSelectedIcon && SelectedIcon ? (
        <SelectedIcon className="h-4 w-4 text-neutral-500" />
      ) : Icon ? (
        <Icon className="h-4 w-4 text-neutral-500" />
      ) : null}
    </button>
  );

  if (tooltipShow) {
    return (
      <TooltipProvider>
        <TooltipWrapper>
          <TooltipTrigger asChild>
            {isDisabled ? <span className="inline-flex">{buttonElement}</span> : buttonElement}
          </TooltipTrigger>
          <TooltipContent className="text-neutral-0 font-sans text-xs leading-none font-normal">
            {tooltipContent}
          </TooltipContent>
        </TooltipWrapper>
      </TooltipProvider>
    );
  } else {
    return buttonElement;
  }
};
